<template>
  <div>
    <LogBookTypeSelector
      v-if="!loading"
      :logtypes="logtypes"
      :logtype.sync="logtype"
      :logs="logs"
      :loading="loading"
    />
    <v-card flat class="ma-2 pa-2 card-rounded">
      <v-card-actions class="mb-2">
          <div class="d-flex overflow-hidden card-rounded border grey--border">
            <v-text-field
              class="mx-1"
              v-model="search"
              prepend-inner-icon="search"
              :label="$t('common.Search')"
              single-line
              hide-details
              solo
              flat
            />
          </div>
          <v-spacer />
          <ExcelExporter
            title="logbooks"
            :entries="displayableLogs"
            :components="logBookComponents"
          ></ExcelExporter>
      </v-card-actions>
      <v-card flat>
        <v-data-table
          v-model="selected"
          :loading="loading"
          :headers="headers"
          class="elevation-0"
          :items="displayableLogs"
          select-all
          :pagination.sync="pagination"
          :search="search"
          :filter="searchFilter"
          :custom-filter="customFilter"
        >
          <template v-slot:headers="props">
            <tr>
              <th>
                <v-checkbox
                  :input-value="props.all"
                  :indeterminate="props.indeterminate"
                  primary
                  hide-details
                  @click.stop="toggleAll"
                />
              </th>
              <th
                v-for="header in props.headers"
                :key="header.text"
                :class="[
                  'column sortable',
                  pagination.descending ? 'desc' : 'asc',
                  header.value === pagination.sortBy ? 'active' : ''
                ]"
                @click="changeSort(header.value)"
              >
                <v-icon small>arrow_upward</v-icon>
                {{ header.text }}
              </th>
            </tr>
          </template>
          <template v-slot:items="props">
            <tr :active="props.selected">
              <td>
                <v-checkbox v-model="props.selected" primary hide-details />
              </td>
              <template>
                <td
                  v-for="component in displayableComponents"
                  :key="component.id"
                  style="text-align: center"
                  @click="() => editLog(props.item.id)"
                >
                  <span
                    v-if="typeof props.item[component.id] === 'boolean'"
                    class="ml-3"
                  >
                    <v-icon v-if="props.item[component.id]" small>check</v-icon>
                  </span>
                  <span v-else>{{ props.item[`${component.id}:text`] }}</span>
                </td>
              </template>
              <td style="text-align: center">
                <v-btn icon @click.stop="() => editLog(props.item.id)">
                  <v-icon small>edit</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
        <portal to="sidebar">
          <v-card flat>
            <v-card-title>
            <Icon size="28px" class="mr-2" name="filter" /> <h3 class="grey--text">Filtros:</h3>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div class="mb-3">
                <v-btn-toggle class="thin-border" v-model="filterLogsByStatus" mandatory>
                  <v-btn flat value="all">{{ $t('common.All') }}</v-btn>
                  <v-btn flat value="incomplete">{{ $t('common.Incomplete') }}</v-btn>
                  <v-btn flat value="complete">{{ $t('common.Complete') }}</v-btn>
                </v-btn-toggle>
              </div>
              <span class="caption grey--text">Tipo de Actividade:</span>
              <v-select
                v-model="logtype"
                :loading="loading"
                label="Logbook"
                item-value="id"
                item-text="label"
                :items="logtypes"
                single-line
                clearable
                solo
                class="thin-border"
              />
            </v-card-text>
          </v-card>
        </portal>
    </v-card>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import ExcelExporter from '@/components/data-exporter/ExcelExporter'
import { Option } from '@/models/Specialty'
import LogBookTypeSelector from '../../Logbook/components/LogbookTypeSelector.vue'

export default {
  props: {
    onEdit: { type: Function, default: () => {} },
    loading: { type: Boolean, default: true },
    logs: { default: [] },
    logtypes: { default: null },
    components: { default: null },
    getSpecialLabel: { type: Function, default: () => null },
    requiredValidators: { type: Object, default: () => ({}) }
  },
  components: {
    ExcelExporter,
    LogBookTypeSelector
  },
  data() {
    return {
      logtype: '',
      search: '',
      selected: [],
      filterLogsByStatus: 'all',
      pagination: {
        sortBy: 'date', // TODO switch to entry-date when avaliable!
        rowsPerPage: 10
      }
    }
  },
  computed: {
    headers() {
      const headers = this.components
        .filter(c => c.show)
        .filter(c => c.logbooks && c.logbooks.includes(this.logtype))
        .map(({ label: text, id: value }) => ({
          text: this.$i18n.locale === 'pt' ? text : value,
          value,
          id: value
        }))
      headers.push({ text: 'Edit', value: 'edit', id: 'edit' })
      return headers
    },
    logBookComponents() {
      return this.components.filter(c => c.logbooks.includes(this.logtype))
    },
    displayableComponents() {
      return this.logBookComponents.filter(c => c.show)
    },
    displayableLogs() {
      const logs = this.labeledLogs
      if(logs) {
        return this.filters
          .filterLogsByStatus(logs, this.filterLogsByStatus)
          .filter(log => log.logbook === this.logtype)
      } else {
        return []
      }
    },
    filters() {
      return {
        filterLogsByStatus: (logs, status) => {
          switch (status) {
            case 'complete':
              return logs.filter(log => !log.incompleted)
            case 'incomplete':
              return logs.filter(log => log.incompleted)
            default:
              return logs
          }
        }
      }
    },
    labeledLogs() {
      let logs = []
      logs = this.logs.map(log => {
        const displayableLog = { id: log.id }
        this.displayableComponents.forEach(c => {
          displayableLog[c.id] = log[c.id]
          displayableLog.logbook = log.logbook
          displayableLog[`${c.id}:text`] = this.getLabel(log, c)
        })
        displayableLog.incompleted = log.incompleted
        return displayableLog
      })
      return logs
    },
    requiredFields() {
      return this.components
        .filter(c => c.required && c.logbooks.includes(this.logtype))
        .map(c => c.id)
    },
    filterCount() {
      let total = 0
      this.logtype ? total += 1 : ''
      this.filterLogsByStatus !== 'all' ? total += 1 : ''
      return total
    }
  },
  methods: {
    ...mapActions(['showErrorSnackbar']),
    deleteSelected(deleteFn) {
      if (this.selected.length) {
        deleteFn(this.selected)
          .then(() => this.showErrorSnackbar(this.$t('common.EntryDeleted')))
          .catch(err =>
            this.showErrorSnackbar(err.message || 'Something went wrong')
          )
      }
    },
    editLog(logId) {
      this.$emit('editLog', logId)
    },
    getLabel(log, component) {
      const item = log[component.id]
      const specialLabel = this.getSpecialLabel(log, component)
      if (specialLabel) {
        return specialLabel
      } else if (
        component.type === 'text-field' ||
        component.type === 'numeric-field' ||
        component.id === 'age'
      ) {
        return item
      } else if (component.type === 'text-area') {
        if (!item) return ''
        return item.length < 50 ? item : `${item.substring(0, 49)}...`
      } else if (component.type === 'date-picker') {
        return this.getDateLabel(item)
      } else if (typeof item === 'string') {
        return this.getOptionLabel(item)
      } else if (Array.isArray(item)) {
        return item.map(e => this.getOptionLabel(e)).join(', ')
      }
    },
    getDateLabel(javascriptTimeStamp = 'invalid') {
      const date = new Date(javascriptTimeStamp)
      let label = ''
      if (date.toString() !== 'Invalid Date') {
        label = date.toLocaleDateString(undefined, { timeZone: 'UTC' })
      }
      return label
    },
    getOptionLabel(id) {
      const option =
        Option.query()
          .where(opt => opt.value === id)
          .first() || {}
      return this.$i18n.locale === 'pt' ? option.text : option.value
    },
    customFilter(logs, pattern, filter) {
      return logs.filter(l => filter(l, pattern))
    },
    searchFilter(logRow, pattern) {
      return (
        !pattern ||
        Object.values(logRow).some(val =>
          String(val)
            .toLowerCase()
            .includes(pattern)
        )
      )
    },
    logCompleted(log) {
      for (const field of this.requiredFields) {
        if (this.requiredValidators[field]) {
          if (!this.requiredValidators[field](log)) return false
        } else if (
          ['null', 'undefined'].includes(typeof log[field]) ||
          log[field] === ''
        ) {
          return false
        }
      }
      return true
    }
  },
  watch: {
    logtypes(logtypes) {
      if (!this.logtype && logtypes.length) {
        this.logtype = logtypes[0].id
      }
    },
    loading(val) {
      if (val === false) {

      }
    }
  },
  created() {
    if (!this.logtype && this.logtypes.length) {
      this.logtype = this.logtypes[0].id
    }
  }
}
</script>
